import * as React from 'react'
import NextLink, { LinkProps as NextLinkProps } from 'next/link'
import { Button, commonButtonStyles } from '@/components/ui/button'

import { cn } from '@/lib/css-utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { omit } from 'lodash'
type BaseLinkProps = {
  className?: string
  variant?:
    | 'default'
    | 'primaryButton'
    | 'secondaryButton'
    | 'outlineButton'
    | 'ghostButton'
    | 'navLink'
    | 'mutedLink'
  size?: 'lg' | 'md' | 'sm'
  buttonProps?: React.ComponentProps<typeof Button>
  linkClassName?: string
}

type InternalLinkProps = NextLinkProps &
  BaseLinkProps & {
    isExternal?: false
  }

type ExternalLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  BaseLinkProps & {
    isExternal: true
  }

const commonLinkStyles = {
  ...commonButtonStyles,
  variants: {
    ...commonButtonStyles.variants,
    default: `text-link-text font-medium hover:text-link-hover-text 
      active:text-link-active-text disabled:text-link-disabled-text border-none`,
    primaryButton: commonButtonStyles.variants.default,
    secondaryButton: commonButtonStyles.variants.secondary,
    outlineButton: commonButtonStyles.variants.outline,
    ghostButton: commonButtonStyles.variants.ghost,
    navLink: `text-topNav-link-text font-medium px-3 py-1.5 bg-topNav-link-bg 
      active:bg-topNav-link-current-bg/20`,
    mutedLink: `text-copy-muted hover:text-copy-primary hover:underline font-normal`,
  },
}

const linkVariants = cva(`${commonButtonStyles.base}`, {
  variants: {
    variant: {
      default: commonLinkStyles.variants.default,
      primaryButton: commonLinkStyles.variants.primaryButton,
      secondaryButton: commonLinkStyles.variants.secondaryButton,
      outlineButton: commonLinkStyles.variants.outlineButton,
      ghostButton: commonLinkStyles.variants.ghostButton,
      navLink: commonLinkStyles.variants.navLink,
      mutedLink: commonLinkStyles.variants.mutedLink,
    },
    size: {
      lg: '',
      md: '',
      sm: '',
    },
  },
  compoundVariants: [
    {
      variant: [
        'primaryButton',
        'secondaryButton',
        'outlineButton',
        'ghostButton',
      ],
      size: 'lg',
      className: 'px-6 py-3 text-md',
    },
    {
      variant: [
        'primaryButton',
        'secondaryButton',
        'outlineButton',
        'ghostButton',
      ],
      size: 'md',
      className: 'px-5 py-2 text-sm',
    },
    {
      variant: [
        'primaryButton',
        'secondaryButton',
        'outlineButton',
        'ghostButton',
      ],
      size: 'sm',
      className: 'px-2.5 py-1 text-xs',
    },
    // Size variants for default links
    {
      variant: 'default',
      size: 'lg',
      className: 'text-md',
    },
    {
      variant: 'default',
      size: 'md',
      className: 'text-sm',
    },
    {
      variant: 'default',
      size: 'sm',
      className: 'text-xs',
    },
  ],
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

type LinkProps = (ExternalLinkProps | InternalLinkProps) &
  VariantProps<typeof linkVariants> & {
    children?: React.ReactNode
  }

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      variant,
      children,
      buttonProps,
      size,
      linkClassName,
      ...props
    }: React.PropsWithChildren<LinkProps>,
    ref,
  ) => {
    const _className = cn(linkVariants({ variant, size }), className)
    if (props.isExternal) {
      const { isExternal: _external, ...rest } = props
      if (
        variant === 'primaryButton' ||
        variant === 'secondaryButton' ||
        variant === 'outlineButton' ||
        variant === 'ghostButton'
      ) {
        return (
          <a target="_blank" rel="noopener noreferrer" ref={ref} {...rest}>
            <Button as="span" {...buttonProps} className={className}>
              {children}
            </Button>
          </a>
        )
      }
      return (
        <a
          target="_blank"
          rel="noopener noreferrer"
          ref={ref}
          className={_className}
          {...rest}
        >
          {children}
        </a>
      )
    }

    const rest = omit(props, 'isExternal')
    if (
      variant === 'primaryButton' ||
      variant === 'secondaryButton' ||
      variant === 'outlineButton' ||
      variant === 'ghostButton'
    ) {
      return (
        <NextLink
          {...rest}
          ref={ref}
          className={linkClassName}
          prefetch={props.prefetch || false}
        >
          <Button as="span" {...buttonProps} className={_className}>
            {children}
          </Button>
        </NextLink>
      )
    }
    return (
      <NextLink
        {...rest}
        ref={ref}
        className={_className}
        prefetch={props.prefetch || false}
      >
        {children}
      </NextLink>
    )
  },
)

Link.displayName = 'Link'
export { Link }
